import { Box, Flex, Skeleton, Stack } from '@chakra-ui/react';
import { Cart, VOUCHER_REWARD_TYPE } from '@diamond/shared/types';
import { LabelPromoList, PromoPriceIntegration } from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import {
  formatDiscountAmountAbbreviated,
  getCartItemStockInfo,
  PromoDisplayData,
} from '@diamond/sol-buyer/utils';

import { CheckIconVoucher } from '../Icons';

interface CartItemPromoPriceProps {
  item: Cart;
  promo: PromoDisplayData;
  isSelected: boolean;
  isLoading?: boolean;
}

export const CartItemPromoPrice = ({
  item,
  promo,
  isSelected,
  isLoading: externalLoading,
}: CartItemPromoPriceProps) => {
  const {
    displayPrice,
    promoDeductionAmount,
    promoIsDiscount,
    isBundled,
    hasActiveDiscount,
    processedPromo,
    voucherDiscount,
    hasVoucherDiscount,
    hasVoucherFreeGoods,
    subtotalAfterDiscount,
  } = promo;

  const stockInfo = getCartItemStockInfo(item);
  const isDisabled = stockInfo.isEmptyStock;
  const showLoading = isSelected && Boolean(externalLoading || promo.isLoading);

  const hasAnyPromo =
    hasVoucherDiscount ||
    hasVoucherFreeGoods ||
    promoIsDiscount ||
    isBundled ||
    hasActiveDiscount ||
    processedPromo.hasFreeGoods ||
    processedPromo.hasBundledFreeGoods ||
    processedPromo.hasMultiFreeGoods;

  const showDiscountDetails =
    hasVoucherDiscount || promoIsDiscount || isBundled || hasActiveDiscount;

  const hasFreeGoodsOnly =
    (processedPromo.hasFreeGoods ||
      processedPromo.hasBundledFreeGoods ||
      processedPromo.hasMultiFreeGoods) &&
    !showDiscountDetails;
  const hasCashbackOnly =
    (processedPromo.hasCashback || processedPromo.hasBundledCashback) &&
    !showDiscountDetails;
  const shouldShowPromoLabel = hasFreeGoodsOnly || hasCashbackOnly;

  return (
    <Stack
      spacing={{ base: '0', lg: '1' }}
      alignItems={{ base: 'flex-start', lg: 'flex-start' }}
      w={{ base: 'auto', lg: 'auto' }}
      id="promo-price-container"
    >
      {hasAnyPromo && (
        <Stack spacing="1" alignItems="flex-start">
          {shouldShowPromoLabel && (
            <Flex gap="2" alignItems="center" flexWrap="nowrap">
              {hasVoucherFreeGoods && isSelected && (
                <CheckIconVoucher
                  boxSize={{ base: '16px', lg: '20px' }}
                  flexShrink={0}
                />
              )}
              <LabelPromoList
                processedPromo={processedPromo}
                isLoading={showLoading}
              />
            </Flex>
          )}
          {showDiscountDetails && !showLoading && (
            <Flex gap="2" alignItems="center" flexWrap="nowrap">
              {(hasVoucherDiscount || hasVoucherFreeGoods) && isSelected && (
                <CheckIconVoucher
                  boxSize={{ base: '16px', lg: '20px' }}
                  flexShrink={0}
                />
              )}
              <Box flexShrink={1}>
                <PromoPriceIntegration
                  data={item.product}
                  promoDeductionAmount={promoDeductionAmount}
                  promoIsDiscount={hasVoucherDiscount || promoIsDiscount}
                  isBundled={!hasVoucherDiscount && isBundled}
                  hasActiveDiscount={hasVoucherDiscount || hasActiveDiscount}
                  badgeLabelOverride={
                    hasVoucherDiscount &&
                    voucherDiscount?.reward_type ===
                      VOUCHER_REWARD_TYPE.DISCOUNT_AMOUNT
                      ? formatDiscountAmountAbbreviated(
                          voucherDiscount.discount_amount
                        )
                      : undefined
                  }
                />
              </Box>
            </Flex>
          )}
        </Stack>
      )}
      <Box textAlign="left" w="full">
        <Skeleton isLoaded={!showLoading} borderRadius="md" minH="20px">
          <Flex
            as="b"
            fontSize={{ base: '14px', md: '16px' }}
            color={isDisabled ? '#8E8E93' : 'inherit'}
            flexDirection={{ base: 'column', lg: 'row' }}
            alignItems="baseline"
          >
            {buyerCurrencyFormatter(displayPrice)}
            <Box
              as="span"
              display="inline-block"
              fontWeight="normal"
              textColor={isDisabled ? '#8E8E93' : 'gray.75'}
              fontSize={{ base: '10px', md: '10px' }}
            >
              /
              <Box
                as="span"
                fontSize={{ base: '10px', md: '10px' }}
                display="inline"
                textTransform="capitalize"
              >
                {item.product.sales_unit}
              </Box>
            </Box>
          </Flex>
        </Skeleton>
      </Box>
    </Stack>
  );
};
